import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
// import { signInWithPopup, signInWithRedirect } from 'firebase/auth'

import { AppButton } from '@/components'
import { useSignInWithGoogle } from '@/domain/auth/useCases/useSignInWithGoogle'
import { useUserAuthSession } from '@/hooks'
import { useProfileDraftStore } from '@/store'
import { useAuthScopedStore } from '@/store/scoped'
// import { auth, googleProvider } from '@/config/firebase'

export const ContinueWithGoogle: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const setIdentificationMethod = useAuthScopedStore(
    (state) => state.setIdentificationMethod,
  )
  const { startSession } = useUserAuthSession()
  const setDraft = useProfileDraftStore((state) => state.setDraft)

  const { mutateAsync: signInWithGoogle } = useSignInWithGoogle({
    onSuccess: async ({ access_token, refresh_token, next_step }) => {
      await startSession({
        token: access_token,
        refreshToken: refresh_token,
      })

      if (next_step === 'SETUP_PROFILE') {
        navigate('/setup-profile')
      } else {
        navigate('/dashboard')
      }
    },
    onError: ({ errorMessage }) => {
      toast.error(errorMessage || 'Not mapped error')
    },
  })

  const processGoogleAuthResult = React.useCallback(
    async (result: any) => {
      const oauthIdToken = result._tokenResponse?.oauthIdToken

      if (!oauthIdToken) {
        throw new Error('No Google OAuth ID token found in response')
      }

      // Get Google avatar URL and ensure it's high quality
      let avatarUrl = result.user.photoURL || ''

      // Google часто возвращает маленькие аватарки (s96-c), увеличиваем размер
      if (avatarUrl) {
        // Заменяем s96-c на s400-c для высокого качества
        avatarUrl = avatarUrl.replace(/=s\d+-c/, '=s400-c')

      }

      const profileData = {
        firstName: result.user.displayName?.split(' ')[0] || '',
        lastName: result.user.displayName?.split(' ').slice(1).join(' ') || '',
        email: result.user.email || '',
        avatar: avatarUrl,
      }

      setDraft(profileData)
      setIdentificationMethod('google')

      await signInWithGoogle({
        id_token: oauthIdToken,
      })
    },
    [signInWithGoogle, setDraft, setIdentificationMethod],
  )

  // Redirect result обрабатывается глобально в FirebaseAuthRedirectHandler
  // Локальная обработка убрана чтобы избежать двойной обработки

  const handleGoogleAuth = async () => {
    try {
      setIdentificationMethod('google')

      // Проверяем запущено ли в Telegram
      const isInTelegram = !!(window as any).Telegram?.WebApp

      try {
        // const result = await signInWithPopup(auth, googleProvider)
        // await processGoogleAuthResult(result)
      } catch (popupError: any) {

        // В Telegram НЕ используем redirect (открывает два окна)
        if (isInTelegram) {
          if (
            popupError.code === 'auth/popup-blocked' ||
            popupError.code === 'auth/popup-closed-by-user'
          ) {
            toast.error(
              'Please allow popups in Telegram settings to sign in with Google',
            )
            return
          }
        }

        // Вне Telegram - используем redirect fallback
        if (
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.message.includes('Cross-Origin-Opener-Policy')
        ) {
          toast('Opening Google authorization...', { duration: 2000 })
          // await signInWithRedirect(auth, googleProvider)
        } else {
          throw popupError
        }
      }
    } catch (error: any) {
      toast.error('Failed to start Google authentication')
    }
  }

  return (
    <AppButton
      iconName="google"
      iconGap={12}
      iconSize={24}
      isDefaultIconColor
      label={t('form:actions.continueWith', { authMethod: 'Google' })}
      onPress={handleGoogleAuth}
      type="secondaryGrayDark"
    />
  )
}
